/**
 * LearnerPassportContextualCard — A1 (Feb 27, 2026 exposure batch).
 *
 * Surfaces the learner Passport surface (currently invisible to its
 * rightful owner) ONLY when the learner has had a lesson stamp
 * within the last 7 days. Operator trigger: post-lesson stamp in
 * last 7 days.
 *
 * Doctrine: learner exposure is contextual + progressive. The card
 * appears AT THE MOMENT it would be meaningful (just had a lesson →
 * passport just got fuller → now is a good time to look at it) and
 * disappears the rest of the time.
 *
 * Reads: GET /api/passport/me — already exists, always free,
 * always accessible per the route's own docstring.
 *
 * Renders: a single calm linked card. No metrics, no operational
 * weight. Mirrors LatestReviewCard / DailyCheckinNudge pattern.
 *
 * See /app/memory/OPERATIONAL_EXPOSURE_MAPPING_V1.md §2.A1.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { IdCard, ChevronRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const FRESHNESS_DAYS = 7;
const FRESHNESS_MS = FRESHNESS_DAYS * 24 * 60 * 60 * 1000;


function _isFreshStamp(iso) {
  if (!iso) return false;
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return false;
  return (Date.now() - ts) <= FRESHNESS_MS;
}


export default function LearnerPassportContextualCard() {
  const [show, setShow] = useState(false);
  const [stampedRecently, setStampedRecently] = useState(false);
  const [totalLessons, setTotalLessons] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/passport/me`, {
          withCredentials: true,
        });
        if (!alive) return;
        const lessons = r.data?.lessons || {};
        const recent = (lessons.recent || []);
        const latest = recent[0]?.completed_at;
        const total = lessons.total_completed || 0;
        const fresh = _isFreshStamp(latest);
        setStampedRecently(fresh);
        setTotalLessons(total);
        setShow(fresh && total > 0);
      } catch {
        // Calm silence — never surface a passport network error on home.
      }
    })();
    return () => { alive = false; };
  }, []);

  if (!show) return null;

  return (
    <Link
      to="/passport"
      data-testid="learner-passport-contextual-card"
      className="
        block rounded-2xl border border-emerald-700/40 bg-emerald-900/15
        px-4 py-3 hover:border-emerald-600/60 hover:bg-emerald-900/25
        transition-colors
      "
    >
      <div className="flex items-center gap-3">
        <IdCard className="w-5 h-5 text-emerald-300 flex-shrink-0" aria-hidden />
        <div className="flex-1 min-w-0">
          <p
            data-testid="learner-passport-contextual-headline"
            className="text-[13.5px] font-bold text-emerald-100"
          >
            Your Passport just got fuller
          </p>
          <p
            data-testid="learner-passport-contextual-detail"
            className="text-[11.5px] text-emerald-200/70 mt-0.5"
          >
            {totalLessons} {totalLessons === 1 ? "lesson stamped" : "lessons stamped"} so far ·
            tap to see your record
          </p>
        </div>
        <ChevronRight className="w-4 h-4 text-emerald-300/70" aria-hidden />
      </div>
    </Link>
  );
}
